import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import VehicleCard from "../components/VehicleCard";

const SORT_OPTS = [
  { key: "latest", label: "Latest" },
  { key: "price_asc", label: "Price: Low to High" },
  { key: "price_desc", label: "Price: High to Low" },
  { key: "year_desc", label: "Newest Year" },
  { key: "km_asc", label: "Lowest Kilometres" },
];

const Cars = () => {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => Object.fromEntries(params.entries()), [params]);

  useEffect(() => { api.get("/vehicles/facets").then((r) => setFacets(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams(filters);
    q.set("limit", "200");
    api.get(`/vehicles?${q.toString()}`).then((r) => { setVehicles(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const FilterBlock = () => (
    <div className="space-y-6 text-sm">
      <FilterSelect label="Brand" value={filters.brand} onChange={(v) => set("brand", v)} options={facets?.brands || []} testId="filter-brand" />
      <FilterSelect label="Body Type" value={filters.body_type} onChange={(v) => set("body_type", v)} options={facets?.body_types || []} testId="filter-body" />
      <FilterSelect label="Fuel" value={filters.fuel} onChange={(v) => set("fuel", v)} options={facets?.fuels || []} testId="filter-fuel" />
      <FilterSelect label="Transmission" value={filters.transmission} onChange={(v) => set("transmission", v)} options={facets?.transmissions || []} testId="filter-transmission" />
      <FilterSelect label="Ownership" value={filters.ownership} onChange={(v) => set("ownership", v)} options={["1st","2nd","3rd","4th+"]} testId="filter-ownership" />
      <div>
        <div className="overline text-[#242424]/60 mb-2">Price</div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Min" value={filters.min_price || ""} onChange={(e) => set("min_price", e.target.value)} className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-min-price" />
          <input type="number" placeholder="Max" value={filters.max_price || ""} onChange={(e) => set("max_price", e.target.value)} className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-max-price" />
        </div>
      </div>
      <div>
        <div className="overline text-[#242424]/60 mb-2">Year</div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="From" value={filters.min_year || ""} onChange={(e) => set("min_year", e.target.value)} className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-min-year" />
          <input type="number" placeholder="To" value={filters.max_year || ""} onChange={(e) => set("max_year", e.target.value)} className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-max-year" />
        </div>
      </div>
      <div>
        <div className="overline text-[#242424]/60 mb-2">Max Kilometres</div>
        <input type="number" placeholder="e.g., 50000" value={filters.max_km || ""} onChange={(e) => set("max_km", e.target.value)} className="w-full bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-max-km" />
      </div>
      <div>
        <div className="overline text-[#242424]/60 mb-2">Colour</div>
        <input type="text" placeholder="e.g., Black" value={filters.colour || ""} onChange={(e) => set("colour", e.target.value)} className="w-full bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-colour" />
      </div>
      <button onClick={clearAll} className="w-full border hairline py-2.5 rounded-sm text-sm hover:bg-white transition-colors" data-testid="filter-clear">
        Clear filters
      </button>
    </div>
  );

  return (
    <div className="pt-8" data-testid="page-cars">
      <div className="container-wide">
        <div className="flex items-end justify-between gap-6 border-b hairline pb-8 mb-8">
          <div>
            <div className="overline text-[#7A1519] mb-3">Inventory</div>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter text-[#111111] font-semibold leading-tight">Buy a Car</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-sm">
              <label className="text-[#242424]/70">Sort</label>
              <select value={filters.sort || "latest"} onChange={(e) => set("sort", e.target.value)} className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid="filter-sort">
                {SORT_OPTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <button onClick={() => setShowFilters(true)} className="lg:hidden inline-flex items-center gap-2 border hairline bg-white px-4 py-2.5 rounded-sm text-sm" data-testid="filter-open-mobile">
              <SlidersHorizontal size={14} /> Filters
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="segment-tabs">
          {[
            { key: "", label: "All Cars" },
            { key: "luxury", label: "Luxury" },
            { key: "standard_premium", label: "Standard Premium" },
          ].map((t) => {
            const active = (filters.segment || "") === t.key;
            return (
              <button
                key={t.key || "all"}
                onClick={() => set("segment", t.key)}
                className={`px-4 py-2 border rounded-sm text-sm transition-colors ${active ? "bg-[#111111] text-white border-[#111111] segment-tab-active" : "bg-white text-[#242424] hover:bg-[#F7F6F3] hairline"}`}
                data-testid={`segment-tab-${t.key || "all"}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
          <aside className="hidden lg:block sticky top-24 self-start">
            <FilterBlock />
          </aside>

          <section>
            <div className="mb-6 text-sm text-[#242424]/70" data-testid="cars-count">
              {loading ? "Loading…" : `${vehicles.length} vehicle${vehicles.length !== 1 ? "s" : ""}`}
            </div>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white border hairline rounded-sm aspect-[4/3] animate-pulse" />
                ))}
              </div>
            ) : vehicles.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 ab-stagger" data-testid="cars-grid">
                {vehicles.map((v) => (
                  <div key={v.id} className="ab-fade-up">
                    <VehicleCard vehicle={v} testIdPrefix="inv" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="border hairline bg-white p-16 text-center text-[#242424]/60" data-testid="cars-empty">
                No vehicles match these filters yet.
              </div>
            )}
          </section>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setShowFilters(false)}>
          <div className="bg-[#F7F6F3] w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-6 rounded-t-sm sm:rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="mobile-filter-panel">
            <div className="flex items-center justify-between mb-6">
              <div className="font-display text-xl font-semibold">Filters</div>
              <button onClick={() => setShowFilters(false)}><X size={20} /></button>
            </div>
            <FilterBlock />
            <button onClick={() => setShowFilters(false)} className="mt-6 w-full bg-[#7A1519] text-white py-3 rounded-sm text-sm font-semibold">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <div className="overline text-[#242424]/60 mb-2">{label}</div>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid={testId}>
      <option value="">Any</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default Cars;
